/**
 * Das Objektblatt — ein Muster für alle Einrichten-Arten (Einrichten-Neubau P2).
 *
 * Kopf mit Zustand und Schalter (außer Betrieb / wieder in Betrieb, zweistufig
 * mit Wirkungs-Satz — bewusst keine „Gefahrenzone"), Stammdaten mit einem
 * Speichern und einer Rückmeldung (die Seite bleibt, wo sie ist), Verwendung
 * mit Zählern und Sprüngen, Verlauf aus dem Protokoll. Die Felder je Art
 * beschreibt arten.ts; geschrieben wird über die bewährten CRUD-Endpunkte.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../App';
import { LeitstandRahmen } from '../../components/leitstand/LeitstandRahmen';
import { Karte, Notiz, Zustand } from '../../components/leitstand';
import { ARTEN, AUSWAHL_QUELLEN, FeldDef } from './arten';

type Werte = Record<string, unknown>;

const EINGABE =
  'w-full rounded-[4px] border border-linie-2 bg-flaeche-2 px-3 py-2 text-sm text-tinte focus:border-akzent focus:outline-none disabled:opacity-60';

export default function Objektblatt() {
  const { user } = useAuth();
  const { art = '', id = '' } = useParams<{ art: string; id: string }>();
  const navigate = useNavigate();
  const def = ARTEN[art];
  const istNeu = id === 'neu';

  const [werte, setWerte] = useState<Werte>({});
  const [aktiv, setAktiv] = useState(true);
  const [laden, setLaden] = useState(!istNeu);
  const [fehler, setFehler] = useState('');
  const [speichert, setSpeichert] = useState(false);
  const [speicherFehler, setSpeicherFehler] = useState('');
  const [gespeichert, setGespeichert] = useState('');

  const [verwendung, setVerwendung] = useState<
    Array<{ text: string; wert: number; ziel?: string }>
  >([]);
  const [verlauf, setVerlauf] = useState<Array<{ wann: string; wer: string; was: string }>>([]);
  const [optionen, setOptionen] = useState<Record<string, Array<{ id: string; name: string }>>>({});

  const [schalterFrage, setSchalterFrage] = useState(false);
  const [schaltet, setSchaltet] = useState(false);

  const lade = useCallback(async () => {
    if (!def || istNeu) return;
    setLaden(true);
    setFehler('');
    try {
      const [oRes, vRes, hRes] = await Promise.all([
        fetch(`/api/einrichten/objekt/${art}/${id}`, { credentials: 'include' }),
        fetch(`/api/einrichten/objekt/${art}/${id}/verwendung`, { credentials: 'include' }),
        fetch(`/api/einrichten/objekt/${art}/${id}/verlauf`, { credentials: 'include' }),
      ]);
      if (!oRes.ok) throw new Error('Das Objekt konnte nicht geladen werden');
      const o = (await oRes.json()).objekt as Werte & { isActive: boolean };
      setWerte(o);
      setAktiv(!!o.isActive);
      if (vRes.ok) setVerwendung((await vRes.json()).zeilen ?? []);
      if (hRes.ok) setVerlauf((await hRes.json()).zeilen ?? []);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Das Objekt konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, [art, id, def, istNeu]);

  useEffect(() => {
    void lade();
  }, [lade]);

  // Auswahl-Optionen (Maschinentypen, Programmtypen) bei Bedarf laden
  useEffect(() => {
    if (!def) return;
    const quellen = [...new Set(def.felder.filter((f) => f.quelle).map((f) => f.quelle as string))];
    for (const q of quellen) {
      if (optionen[q]) continue;
      void (async () => {
        try {
          const res = await fetch(AUSWAHL_QUELLEN[q], { credentials: 'include' });
          if (!res.ok) return;
          const body = await res.json();
          const liste = Array.isArray(body)
            ? body
            : (body.items ?? body.machineTypes ?? body.programTypes ?? []);
          setOptionen((prev) => ({ ...prev, [q]: liste }));
        } catch {
          /* Auswahl bleibt leer — Speichern meldet es */
        }
      })();
    }
  }, [def, optionen]);

  if (!def) {
    return (
      <LeitstandRahmen titel="Katalog" benutzer={user?.displayName}>
        <Notiz titel="Unbekannte Objektart">
          Diese Art gibt es im Katalog nicht.{' '}
          <Link to="/einrichten" className="underline">
            Zum Katalog
          </Link>
        </Notiz>
      </LeitstandRahmen>
    );
  }

  const sichtbareFelder = def.felder.filter((f) => istNeu || !f.nurBeiAnlage);

  const speichere = async () => {
    setSpeicherFehler('');
    setGespeichert('');
    for (const f of sichtbareFelder) {
      if (f.pflicht && !String(werte[f.key] ?? '').trim()) {
        setSpeicherFehler(`Bitte „${f.label}" ausfüllen.`);
        return;
      }
    }
    const plausi = def.pruefe?.(werte);
    if (plausi) {
      setSpeicherFehler(plausi);
      return;
    }

    const body: Werte = {};
    for (const f of sichtbareFelder) {
      if (!istNeu && f.festNachAnlage) continue;
      const roh = werte[f.key];
      body[f.key] =
        f.typ === 'zahl'
          ? roh === '' || roh === null || roh === undefined
            ? null
            : Number(roh)
          : typeof roh === 'string'
            ? roh.trim() || null
            : (roh ?? null);
    }

    setSpeichert(true);
    try {
      const res = await fetch(istNeu ? def.api.pfad : `${def.api.pfad}/${id}`, {
        method: istNeu ? 'POST' : def.api.updateMethode,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const antwort = await res.json().catch(() => null);
      if (!res.ok) throw new Error(antwort?.error || 'Speichern fehlgeschlagen');
      if (istNeu) {
        const neueId =
          antwort?.id ??
          antwort?.objekt?.id ??
          Object.values(antwort ?? {}).find(
            (v): v is { id: string } => !!v && typeof v === 'object' && 'id' in (v as object),
          )?.id;
        navigate(neueId ? `/einrichten/${art}/${neueId}` : `/einrichten/${art}`, { replace: true });
        return;
      }
      setGespeichert('✓ Gespeichert');
      setTimeout(() => setGespeichert(''), 2600);
      void lade();
    } catch (e) {
      setSpeicherFehler(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSpeichert(false);
    }
  };

  const schalteZustand = async (ziel: boolean) => {
    setSchaltet(true);
    setSpeicherFehler('');
    try {
      const res = await fetch(`/api/einrichten/objekt/${art}/${id}/zustand`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ aktiv: ziel }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || 'Der Zustand konnte nicht geändert werden');
      }
      setAktiv(ziel);
      setSchalterFrage(false);
      void lade();
    } catch (e) {
      setSpeicherFehler(
        e instanceof Error ? e.message : 'Der Zustand konnte nicht geändert werden',
      );
    } finally {
      setSchaltet(false);
    }
  };

  const feldEingabe = (f: FeldDef) => {
    const wert = werte[f.key] ?? '';
    const gesperrt = !istNeu && !!f.festNachAnlage;
    const setze = (v: unknown) => setWerte((prev) => ({ ...prev, [f.key]: v }));
    if (f.typ === 'auswahl') {
      const liste = optionen[f.quelle ?? ''] ?? [];
      return (
        <select
          value={String(wert)}
          onChange={(e) => setze(e.target.value)}
          disabled={gesperrt}
          className={EINGABE}
        >
          <option value="">— bitte wählen —</option>
          {liste.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      );
    }
    if (f.typ === 'mehrzeilig') {
      return (
        <textarea
          value={String(wert)}
          onChange={(e) => setze(e.target.value)}
          rows={2}
          disabled={gesperrt}
          className={EINGABE}
        />
      );
    }
    return (
      <input
        type={f.typ === 'zahl' ? 'number' : 'text'}
        value={String(wert)}
        onChange={(e) => setze(e.target.value)}
        disabled={gesperrt}
        className={EINGABE}
      />
    );
  };

  const titel = istNeu ? `${def.einzahl} anlegen` : String(werte.name ?? def.einzahl);

  return (
    <LeitstandRahmen
      titel={titel}
      benutzer={user?.displayName}
      werkzeuge={
        !istNeu && !laden ? (
          <span className="flex items-center gap-3">
            {aktiv ? (
              <Zustand art="gut">aktiv</Zustand>
            ) : (
              <span className="font-zahl text-[11px] text-tinte-3">außer Betrieb</span>
            )}
            <button
              onClick={() => (aktiv ? setSchalterFrage(true) : void schalteZustand(true))}
              disabled={schaltet}
              className="rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 font-zahl text-xs text-tinte-2 hover:border-linie-stark disabled:opacity-50"
            >
              {aktiv ? 'Außer Betrieb nehmen' : 'Wieder in Betrieb nehmen'}
            </button>
          </span>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-6">
        <Link to={`/einrichten/${art}`} className="text-[13px] text-tinte-3 hover:text-tinte-2">
          ← {def.mehrzahl}
        </Link>

        {schalterFrage && (
          <div className="flex flex-wrap items-center gap-3 rounded-leitstand border border-linie-2 bg-flaeche px-4 py-3">
            <span className="text-[13.5px] text-tinte-2">{def.zustandsWirkung}</span>
            <button
              onClick={() => void schalteZustand(false)}
              disabled={schaltet}
              className="rounded-[4px] border border-akzent bg-akzent px-3 py-1.5 font-zahl text-xs text-akzent-satz disabled:opacity-50"
            >
              Außer Betrieb nehmen
            </button>
            <button
              onClick={() => setSchalterFrage(false)}
              disabled={schaltet}
              className="rounded-[4px] border border-linie-2 bg-flaeche px-3 py-1.5 font-zahl text-xs text-tinte-2"
            >
              Abbrechen
            </button>
          </div>
        )}

        {fehler && (
          <Notiz titel="Konnte nicht laden">
            {fehler}{' '}
            <button onClick={() => void lade()} className="underline">
              Erneut versuchen
            </button>
          </Notiz>
        )}

        {laden && !fehler && (
          <p className="py-12 text-center text-sm text-tinte-3">Lade {def.einzahl} …</p>
        )}

        {!laden && !fehler && (
          <>
            <Karte titel="Stammdaten" marke="ein Speichern, eine Rückmeldung">
              <div className="flex flex-col gap-4">
                <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
                  {sichtbareFelder.map((f) => (
                    <label key={f.key} className="flex flex-col gap-1.5">
                      <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                        {f.label}
                        {f.pflicht ? ' *' : ''}
                      </span>
                      {feldEingabe(f)}
                      {f.hinweis && <span className="text-xs text-tinte-3">{f.hinweis}</span>}
                    </label>
                  ))}
                </div>
                {!istNeu && typeof werte.code === 'string' && werte.code && (
                  <details className="text-[12.5px] text-tinte-3">
                    <summary className="cursor-pointer font-zahl text-[11px] uppercase tracking-[0.13em]">
                      Technische Angaben
                    </summary>
                    <p className="mt-1.5">
                      Kürzel <span className="font-zahl text-tinte-2">{String(werte.code)}</span> —
                      vom System vergeben; Geräte und Historie hängen daran, deshalb unveränderlich.
                    </p>
                  </details>
                )}
                {speicherFehler && <p className="text-[13px] text-stoer">{speicherFehler}</p>}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => void speichere()}
                    disabled={speichert}
                    className="rounded-[4px] border border-akzent bg-akzent px-4 py-2 font-zahl text-xs text-akzent-satz disabled:opacity-50"
                  >
                    {istNeu ? 'Anlegen' : 'Speichern'}
                  </button>
                  {gespeichert && (
                    <span className="font-zahl text-[13px] text-lauf">{gespeichert}</span>
                  )}
                </div>
              </div>
            </Karte>

            {!istNeu && (
              <Karte titel="Verwendung" marke="Wirkung sichtbar, bevor man handelt">
                {verwendung.length === 0 ? (
                  <p className="py-4 text-center text-sm text-tinte-3">Noch nirgends verwendet.</p>
                ) : (
                  <div className="flex flex-col">
                    {verwendung.map((v, i) => {
                      const inhalt = (
                        <>
                          <span className="font-zahl text-[15px] font-medium">
                            {v.wert.toLocaleString('de-DE')}
                          </span>
                          <span className="text-sm">{v.text}</span>
                          {v.ziel && <span className="ml-auto font-zahl text-tinte-3">→</span>}
                        </>
                      );
                      const klasse = `flex items-center gap-3 px-1 py-3 ${i < verwendung.length - 1 ? 'border-b border-linie' : ''}`;
                      return v.ziel ? (
                        <Link key={v.text} to={v.ziel} className={`${klasse} hover:bg-flaeche-2`}>
                          {inhalt}
                        </Link>
                      ) : (
                        <div key={v.text} className={klasse}>
                          {inhalt}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Karte>
            )}

            {!istNeu && (
              <Karte titel="Verlauf" marke="aus dem Protokoll, neueste zuerst">
                {verlauf.length === 0 ? (
                  <p className="py-4 text-center text-sm text-tinte-3">
                    Noch keine Einträge im Protokoll.
                  </p>
                ) : (
                  <div className="flex flex-col">
                    {verlauf.map((z, i) => (
                      <div
                        key={`${z.wann}-${i}`}
                        className={`grid grid-cols-[110px_130px_1fr] gap-3 px-1 py-2.5 text-[13px] max-sm:grid-cols-[90px_1fr] ${
                          i < verlauf.length - 1 ? 'border-b border-linie' : ''
                        }`}
                      >
                        <span className="font-zahl text-xs text-tinte-3">
                          {new Date(z.wann).toLocaleString('de-DE', {
                            timeZone: 'Europe/Berlin',
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        <span className="text-tinte-2">{z.wer}</span>
                        <span className="max-sm:col-span-2">{z.was}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Karte>
            )}
          </>
        )}
      </div>
    </LeitstandRahmen>
  );
}
